import React from "react";
import { Modal, Button } from "@repo/core-ui";
import { Loader2, Save } from "lucide-react";
import { HotelListItem, UpdateHotelRequest } from "@repo/types";
import { HotelHeader } from "./HotelHeader";
import { BasicInfoSection } from "./BasicInfoSection";
import { ContactSection } from "./ContactSection";
import { TimingSection } from "./TimingSection";
import { TaxSection } from "./TaxSection";

interface HotelProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedHotel: HotelListItem | null;
  isEditMode: boolean;
  isUpdating: boolean;
  handleSave: () => void;
  editForm: Partial<UpdateHotelRequest>;
  setEditForm: (form: Partial<UpdateHotelRequest>) => void;
  logoPreview: string | null;
  handleLogoChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isUploadingLogo: boolean;
}

export const HotelProfileModal: React.FC<HotelProfileModalProps> = React.memo(
  ({
    isOpen,
    onClose,
    selectedHotel,
    isEditMode,
    isUpdating,
    handleSave,
    editForm,
    setEditForm,
    logoPreview,
    handleLogoChange,
    isUploadingLogo,
  }) => {
    if (!selectedHotel) return null;

    return (
      <Modal
        open={isOpen}
        onClose={onClose}
        title="Hotel Information"
        maxWidth="max-w-3xl"
        maxHeight="max-h-[95vh] sm:max-h-[90vh]"
        footer={
          <div className="flex justify-end items-center w-full gap-3">
            {isEditMode ? (
              <>
                <Button
                  onClick={handleSave}
                  className="bg-blue-600 dark:bg-blue-500 text-white hover:bg-blue-700 dark:hover:bg-blue-600 border-none px-8 rounded-xl flex items-center gap-2 shadow-lg shadow-blue-500/20"
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Save size={16} />
                  )}
                  {isUpdating ? "Saving..." : "Save Changes"}
                </Button>
              </>
            ) : (
              <div className="flex justify-end w-full">
                <Button
                  onClick={onClose}
                  className="bg-[#111c44] text-white hover:bg-black dark:hover:bg-[#1b254b] border-none px-8 rounded-xl shadow-lg shadow-black/20"
                >
                  Close Details
                </Button>
              </div>
            )}
          </div>
        }
      >
        <div className="space-y-6 py-3 px-1">
          {/* ===== PREMIUM HEADER ===== */}
          <HotelHeader
            selectedHotel={selectedHotel}
            logoPreview={logoPreview}
            isEditMode={isEditMode}
            isUploadingLogo={isUploadingLogo}
            handleLogoChange={handleLogoChange}
          />

          {/* ===== GRID SECTION ===== */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <BasicInfoSection
              editForm={editForm}
              isEditMode={isEditMode}
              setEditForm={setEditForm}
              salesmanCode={selectedHotel.SalesmanCode || ""}
            />

            <ContactSection
              editForm={editForm}
              isEditMode={isEditMode}
              setEditForm={setEditForm}
            />

            <TimingSection
              editForm={editForm}
              isEditMode={isEditMode}
              setEditForm={setEditForm}
              isActive={selectedHotel.IsActive ?? false}
              proExpireDate={selectedHotel.ProExpireDate}
            />

            <TaxSection
              editForm={editForm}
              isEditMode={isEditMode}
              setEditForm={setEditForm}
              selectedHotel={selectedHotel}
            />

            {/* <TaxSection
            editForm={editForm}
            isEditMode={isEditMode}
            setEditForm={setEditForm}
          /> */}
          </div>
        </div>
      </Modal>
    );
  },
);
